import { useEffect, useRef, useState } from 'react';

import type { PipelineRun } from '@clm/shared-types';

import { cancelPipelineRun, getPipelineRun } from '../../api/pipeline-runs';
import { mayAttempt } from '../../auth/permissions';
import { useIdentity } from '../../auth/useIdentity';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { Tile } from '../quality/panel';
import {
  ACTIVE_POLL_MS,
  APPROVE_ROLES,
  STALE_HEARTBEAT_MS,
  isTerminal,
  runProblemHead,
  toRunProblem,
  type RunProblem,
} from './shared';

/**
 * The one `running` row — CLM-83 (D-83-6, D-83-7).
 *
 * Polls `get-pipeline-run` every 5 s while mounted; the interval is cleared on
 * unmount and as soon as the row turns terminal, at which point `onFinished` is
 * called **once** so the parent reloads the list and the status tiles. A
 * heartbeat older than two minutes renders with the `warn` tone — the worker
 * heartbeats once a minute, so a stale one means the API instance running it
 * is gone (the claim statement re-claims it after ten minutes).
 *
 * **Cancel** is for admins (display hint; the API decides). Counts are
 * whatever the row holds — filled at finish today, since the runner exposes no
 * per-contract hook.
 */
export function ActiveRunCard({
  run: initial,
  onFinished,
  now = () => Date.now(),
}: {
  readonly run: PipelineRun;
  readonly onFinished: () => void;
  /** Injectable clock for the stale computation. */
  readonly now?: () => number;
}) {
  const { identity } = useIdentity();
  const mayCancel = mayAttempt(identity, APPROVE_ROLES);
  const [run, setRun] = useState<PipelineRun>(initial);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<RunProblem | null>(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    setRun(initial);
    finishedRef.current = false;
  }, [initial]);

  useEffect(() => {
    if (isTerminal(run)) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished();
      }
      return;
    }
    const controller = new AbortController();
    const timer = setInterval(() => {
      void getPipelineRun(run.pipeline_run_id, controller.signal).then(
        (response) => {
          if (!controller.signal.aborted) setRun(response.run);
        },
        () => undefined,
      );
    }, ACTIVE_POLL_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [run, onFinished]);

  const cancel = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const response = await cancelPipelineRun(run.pipeline_run_id);
      setRun(response.run);
    } catch (error) {
      setProblem(toRunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const heartbeatAge = run.heartbeat_utc === null ? null : now() - Date.parse(run.heartbeat_utc);
  const stale = heartbeatAge !== null && heartbeatAge > STALE_HEARTBEAT_MS;

  return (
    <div className="card" role="group" aria-label="Active run" data-status={run.status} data-stale={stale ? 'true' : 'false'}>
      <p>
        <strong>{run.status}</strong> — started{' '}
        {run.started_utc === null ? '—' : <time dateTime={run.started_utc}>{run.started_utc}</time>} · approved by{' '}
        {run.approved_by_upn ?? '—'}
      </p>
      <p className="mut" data-tone={stale ? 'warn' : 'plain'}>
        Last heartbeat {run.heartbeat_utc === null ? '—' : <time dateTime={run.heartbeat_utc}>{run.heartbeat_utc}</time>}
        {stale ? ' (stale — the worker has not reported for over two minutes)' : ''}
      </p>
      <div className="totals" role="group" aria-label="Run counts">
        <Tile label="Seen" value={run.counts.seen} />
        <Tile label="Written" value={run.counts.written} />
        <Tile label="Held" value={run.counts.held} tone={run.counts.held > 0 ? 'warn' : undefined} />
        <Tile label="Failed" value={run.counts.failed} tone={run.counts.failed > 0 ? 'bad' : undefined} />
      </div>
      <p className="mut">
        Correlation{' '}
        <code>{run.correlation_id}</code>{' '}
        <button
          type="button"
          className="btn qt sm"
          aria-label="Copy correlation id"
          onClick={() => void navigator.clipboard?.writeText(run.correlation_id)}
        >
          Copy
        </button>
      </p>
      {isTerminal(run) ? null : (
        <div className="vacts">
          <button
            type="button"
            className="btn sm"
            disabled={!mayCancel || busy}
            title={mayCancel ? 'Stops after the current batch; the row ends cancelled with its counts' : 'Admin role required'}
            aria-label={`Cancel run ${run.pipeline_run_id}`}
            onClick={() => void cancel()}
          >
            Cancel
          </button>
        </div>
      )}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{runProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
